import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft, ExternalLink, Layers, Lock, Mail, MapPin, Package, Pencil, Phone, Trash2 } from 'lucide-react';
import type { Beneficiary } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, ConfirmDialog, TableSkeleton } from '../../components/ui';
import { ExportPackageDialog } from './PackageDialogs';
import { AttachmentsPanel, ContactsPanel, MasterBadge, NotesThread, TeamPicker, VisibilityBadge, VisibilityControl } from '../../components/records';
import { EnrolmentsPanel } from './EnrolmentsPanel';
import { ProfileSections } from './Sections';
import { BeneficiaryLogo, KindBadge, Label, ScoreBar, fmtNumber, useBeneficiaryConfig } from './shared';

/** One beneficiary: type-aware header and details, enrolments with impacts, profile sections, classification links and the shared panels. */
export function BeneficiaryPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me, canAction } = useAuth();
  const [handOver, setHandOver] = useState(false);
  const q = useQuery({ queryKey: ['beneficiary', id], queryFn: () => api.get<Beneficiary>(`/api/beneficiaries/${id}`) });
  const config = useBeneficiaryConfig();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({ mutationFn: () => api.delete(`/api/beneficiaries/${id}`), onSuccess: () => { toast.success(t('beneficiaries.deleted')); void qc.invalidateQueries({ queryKey: ['beneficiaries'] }); void navigate({ to: '/beneficiaries', search: {} }); }, onError: (e) => toast.error(describeError(e, t)) });

  if (q.isLoading) return <TableSkeleton />;
  if (!q.data) return <div className="card p-10 text-center text-sm text-neutral-500">{t('errors.notFound')}</div>;
  const b = q.data;
  const name = pick(i18n.language, b.name_en, b.name_ar);
  const editable = can('beneficiaries', 'can_edit') || b.created_by === me?.id;
  const canDelete = can('beneficiaries', 'can_delete') || b.created_by === me?.id;
  const classificationOn = Boolean(config.data?.classification_enabled) && b.kind === 'organisation';

  return (
    <div>
      <nav className="mb-2 flex flex-wrap items-center gap-1 text-xs text-neutral-500">
        <Link to="/beneficiaries" search={{ kind: b.kind }} className="hover:underline">{t(`beneficiaries.kindsPlural.${b.kind}`)}</Link>
        <span>›</span><span dir="auto">{name}</span>
      </nav>
      <div className="card mb-4 flex flex-wrap items-start gap-4 p-4">
        <BeneficiaryLogo url={b.logo_url} name={name} kind={b.kind} size={80} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold" dir="auto">{name}</h1>
            <KindBadge kind={b.kind} />
            <VisibilityBadge visibility={b.visibility} />
            <MasterBadge master={b.master} />
            {!b.is_active && <Badge tone="danger">{t('users.inactive')}</Badge>}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">{b.types.map((x) => <Badge key={x.code} tone={b.kind === 'individual' ? 'info' : 'neutral'}>{pick(i18n.language, x.name_en, x.name_ar)}</Badge>)}</div>
          {(b.brief_en || b.brief_ar) && <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300" dir="auto">{pick(i18n.language, b.brief_en, b.brief_ar)}</p>}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-500">
            {b.region && <span><MapPin size={12} className="me-1 inline text-red-500" /><Label value={b.region} /></span>}
            {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-700 hover:underline dark:text-brand-100"><ExternalLink size={12} /> {b.website}</a>}
            {b.email && <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:underline" dir="ltr"><Mail size={12} /> {b.email}</a>}
            {b.phone && <span dir="ltr" className="inline-flex items-center gap-1"><Phone size={12} /> {b.phone}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/beneficiaries', search: { kind: b.kind } })}><ArrowLeft size={16} className="rtl:rotate-180" /> {t('app.back')}</button>
          {canAction('beneficiaries.export') && b.kind === 'individual' && <button type="button" className="btn-secondary" onClick={() => setHandOver(true)}><Package size={16} /> {t('beneficiaries.package.handOver')}</button>}
          {canDelete && <button type="button" className="btn-danger" onClick={() => setConfirmDelete(true)}><Trash2 size={16} /> {t('app.delete')}</button>}
          {editable && <button type="button" className="btn-primary" onClick={() => void navigate({ to: '/beneficiaries/$id/edit', params: { id } })}><Pencil size={16} /> {t('app.edit')}</button>}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="grid grid-cols-1 content-start gap-4">
          {b.organisation && (
            <div className="card p-4">
              <h2 className="mb-2 font-semibold">{t('beneficiaries.sections.company')}</h2>
              <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                <Fact label={t('beneficiaries.crName')} value={pick(i18n.language, b.organisation.cr_name_en, b.organisation.cr_name_ar)} />
                <Fact label={t('beneficiaries.crNumber')} value={b.organisation.cr_number} ltr />
                <Fact label={t('beneficiaries.unifiedNumber')} value={b.organisation.unified_number} ltr />
                <Fact label={t('beneficiaries.capital')} value={b.organisation.capital === null ? null : `${fmtNumber(b.organisation.capital, i18n.language)} SAR`} ltr />
                <Fact label={t('beneficiaries.companySize')} value={b.organisation.company_size ? pick(i18n.language, b.organisation.company_size.name_en, b.organisation.company_size.name_ar) : null} />
                <Fact label={t('beneficiaries.officeType')} value={b.organisation.office_type ? pick(i18n.language, b.organisation.office_type.name_en, b.organisation.office_type.name_ar) : null} />
                <Fact label={t('beneficiaries.address')} value={pick(i18n.language, b.organisation.address_en, b.organisation.address_ar)} />
              </dl>
              <ScoreBar value={b.organisation.local_content_score} className="mt-3 max-w-sm" />
            </div>
          )}
          {b.individual && (
            <div className="card p-4">
              <h2 className="mb-2 font-semibold">{t('beneficiaries.sections.personal')}</h2>
              <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                <Fact label={t('beneficiaries.nationalId')} value={b.individual.national_id} ltr masked={b.individual.national_id_masked} />
                <Fact label={t('beneficiaries.nationality')} value={b.individual.nationality ? pick(i18n.language, b.individual.nationality.name_en, b.individual.nationality.name_ar) : null} />
                <Fact label={t('beneficiaries.genderLabel')} value={b.individual.gender ? t(`beneficiaries.gender.${b.individual.gender}`) : null} />
                <Fact label={t('beneficiaries.dateOfBirth')} value={b.individual.date_of_birth_masked ? '••••-••-••' : b.individual.date_of_birth} ltr masked={b.individual.date_of_birth_masked} />
                <Fact label={t('beneficiaries.city')} value={pick(i18n.language, b.individual.city_en, b.individual.city_ar)} />
                <Fact label={t('beneficiaries.address')} value={pick(i18n.language, b.individual.address_en, b.individual.address_ar)} />
              </dl>
              {(b.individual.national_id_masked || b.individual.date_of_birth_masked) && <p className="mt-2 flex items-center gap-1 text-xs text-neutral-500"><Lock size={12} /> {t('beneficiaries.maskedNotice')}</p>}
            </div>
          )}
          {classificationOn && (
            <div className="card flex flex-wrap items-center justify-between gap-2 p-4">
              <div>
                <h2 className="flex items-center gap-2 font-semibold"><Layers size={16} className="text-neutral-500" />{t('beneficiaries.classification.title')}</h2>
                <p className="text-xs text-neutral-500">{t('beneficiaries.classification.subtitle', { count: b.activity_count })}</p>
              </div>
              <Link to="/beneficiaries/$id/classification" params={{ id }} className="btn-secondary">{t('beneficiaries.classification.open')}</Link>
            </div>
          )}
          {can('enablement_programs') && <EnrolmentsPanel beneficiary={b} editable={editable} />}
          <ProfileSections beneficiary={b} editable={editable} enabled={config.data?.enabled ?? []} />
          {b.kind === 'organisation' && <ContactsPanel ownerType="beneficiary" ownerId={id} canEdit={editable} />}
          <NotesThread ownerType="beneficiary" ownerId={id} canModerate={can('beneficiaries', 'can_delete')} />
        </div>
        <div className="grid grid-cols-1 content-start gap-4">
          <VisibilityControl ownerType="beneficiary" ownerId={id} />
          <TeamPicker ownerType="beneficiary" ownerId={id} canEdit={editable} title={t('beneficiaries.team')} />
          <AttachmentsPanel ownerType="beneficiary" ownerId={id} canEdit={editable} />
          {b.notes && <div className="card p-4"><div className="label">{t('stakeholders.notes')}</div><p className="whitespace-pre-wrap text-sm" dir="auto">{b.notes}</p></div>}
        </div>
      </div>
      {handOver && <ExportPackageDialog ids={[b.id]} title={pick(i18n.language, b.name_en, b.name_ar)} onClose={() => setHandOver(false)} />}
      <ConfirmDialog open={confirmDelete} title={t('beneficiaries.deleteTitle')} body={t('beneficiaries.deleteBody', { name })} danger onCancel={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); remove.mutate(); }} />
    </div>
  );
}

function Fact({ label, value, ltr, masked }: { label: string; value: string | null | undefined; ltr?: boolean; masked?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="flex items-center gap-1" dir={ltr ? 'ltr' : 'auto'}>{masked && <Lock size={12} className="text-neutral-400" />}{value || '—'}</dd>
    </div>
  );
}
